import { ResolvedToolDefinition } from '@justrelate/llm-conversation';
import { parse as parsePartialJson } from 'partial-json';
import { ComponentType } from 'react';
import { ClientError, DataClass, load } from 'scrivito';
import { ZodType, z } from 'zod';

import { defineCRUDToolsV1 } from './crud_tools_v1';
import { defineCRUDToolsV2 } from './crud_tools_v2';
import { formatObj } from './format_obj';

export async function buildDataModelDescription(classes: DataClass[]) {
  const messages = await Promise.all(
    classes.map(async (dataClass) => {
      const attributes = filterEnumValues(
        await load(() => dataClass.attributeDefinitions()),
      );

      return `Data class ${dataClass.name()} with the attributes:
      ${JSON.stringify(attributes)}

      `;
    }),
  );

  return messages.join('\n');
}

function filterEnumValues(
  attributes: Record<string, [string, object]>,
): Record<string, unknown> {
  const filteredAttributes: Record<string, unknown> = {};

  for (const [key, [type, details]] of Object.entries(attributes)) {
    // @ts-expect-error: ignore for prototype
    if (details?.values) {
      // @ts-expect-error: ignore for prototype
      const { _values, ...rest } = details;
      filteredAttributes[key] = [type, rest];
    } else {
      filteredAttributes[key] = [type, details];
    }
  }

  return filteredAttributes;
}

export type ToolDefinition<T = unknown> =
  | TypedToolDefinition<T>
  | StreamingToolDefinition
  | ComponentToolDefinition<T>;

interface TypedToolDefinition<T> {
  name: string;
  description: string;
  parameters: z.ZodObject & z.ZodType<T>;
  explanation?: (args: T) => string;
  run: (args: T) => Promise<unknown>;
  runStreaming?: never;
  render?: never;
}

export interface StreamingToolDefinition {
  name: string;
  description: string;
  parameters: z.ZodObject;
  explanation?: (args: ToolArgsSnapshot) => string | undefined;
  run?: never;
  runStreaming: (
    argsStream: AsyncIterable<ToolArgsSnapshot>,
  ) => Promise<string>;
  render?: never;
}

export interface ComponentToolDefinition<T> {
  name: string;
  description: string;
  parameters: z.ZodObject & z.ZodType<T>;
  render: ComponentType<T & object>;
  run?: never;
  runStreaming?: never;
}

export type ToolArgsSnapshot = Record<string, unknown>;

export interface WebsiteConfig {
  instanceId: string;
  baseUrl: string;
  siteId: string;
}

export function defineSiteSearchTool(sites: Record<string, WebsiteConfig>) {
  return resolveTool({
    name: 'site-search',
    description: 'Search various corporate websites for information. ',
    parameters: z.object({
      site: z.enum(Object.keys(sites) as [string, ...string[]]),
      searchTerm: z.string().describe('The search term should be in english.'),
      alternativeSearchTerms: z.array(z.string()),
    }),
    run: async ({ searchTerm, site }) => {
      // scrivito.com
      const website = sites[site as keyof typeof sites];

      if (!website) {
        throw new Error(`Invalid website ${site}`);
      }

      const instance = website.instanceId;

      const response = await fetch(
        `https://beta-api.scrivito.com/tenants/${instance}/perform`,
        {
          method: 'PUT',
          headers: { 'content-type': 'application/json; charset=utf-8' },
          body: JSON.stringify({
            path: 'workspaces/published/objs/search',
            verb: 'GET',
            params: {
              query: [
                {
                  field: '_site_id',
                  operator: 'equals',
                  value: [website.siteId],
                },
                { field: '*', operator: 'matches', value: [searchTerm] },
              ],
              options: { site_aware: true },
              size: 10,
              include_objs: true,
            },
          }),
          mode: 'cors',
          credentials: 'omit',
        },
      );

      const { objs } = (await response.json()) as {
        objs: Record<string, unknown>[];
      };

      const formatted = await Promise.all(
        objs.map(async (result: Record<string, unknown>) => {
          const { _id, ...rest } = result;

          return {
            _url: `${website.baseUrl}/${_id}`,
            text: await formatObj({ ...rest }),
          };
        }),
      );

      // eslint-disable-next-line no-console
      console.log('pages', formatted);

      return formatted;
    },
  });
}

export function resolveTool<T>(
  tool: ToolDefinition<T>,
): ResolvedToolDefinition {
  if (tool.render) return resolveComponentTool(tool);

  return tool.runStreaming
    ? resolveStreamingTool(tool)
    : resolveTypedTool(tool);
}

function resolveTypedTool<T>({
  name,
  run,
  parameters,
  description,
  explanation,
}: TypedToolDefinition<T>): ResolvedToolDefinition {
  return {
    name,
    description,
    explanation: (args: string) => {
      try {
        const parsed = parameters.parse(JSON.parse(args || '{}'));

        return explanation?.(parsed);
      } catch {
        return;
      }
    },
    parameters: convertToJsonSchema(parameters),
    function: async (input: string) => {
      // eslint-disable-next-line no-console
      console.log(`[model performs '${name}' with ${input}]`);

      try {
        const parsed = parameters.parse(JSON.parse(input || '{}'));

        const result = await run(parsed);

        const message =
          typeof result === 'string' ? result : JSON.stringify(result);

        return message;
      } catch (error) {
        if (!(error instanceof ClientError)) {
          const message = `Error: ${error}`;

          // eslint-disable-next-line no-console
          console.log('some error', error);

          // TODO is this safe?
          return message;
        }

        const message = `Error: ${error.message}, ${error.code}, ${JSON.stringify(error.details)}`;

        // eslint-disable-next-line no-console
        console.log('client error', message);

        return message;
      }
    },
  };
}

function resolveComponentTool<T>({
  name,
  description,
  parameters,
}: ComponentToolDefinition<T>): ResolvedToolDefinition {
  return resolveTypedTool({
    name,
    description,
    parameters,
    run: async () => 'Component displayed to the user.',
  });
}

function convertToJsonSchema(zodObject: ZodType): Record<string, unknown> {
  return z.toJSONSchema(zodObject);
}

function resolveStreamingTool({
  name,
  runStreaming,
  parameters,
  description,
  explanation,
}: StreamingToolDefinition): ResolvedToolDefinition {
  return {
    name,
    description,
    explanation: (args) => {
      if (!explanation) return;

      const partialArgs = parsePartialArgs(args);
      if (!partialArgs) return;

      return explanation(partialArgs);
    },
    parameters: convertToJsonSchema(parameters),
    functionStreaming: async (argsStream: AsyncIterable<string>) => {
      try {
        return await runStreaming(parsePartialArgsStream(argsStream));
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(`tool "${name}" failed`, error);
        return `Error: ${error instanceof Error ? error.message : String(error)}`;
      }
    },
  };
}

async function* parsePartialArgsStream(
  argsStream: AsyncIterable<string>,
): AsyncIterable<ToolArgsSnapshot> {
  for await (const args of argsStream) {
    const partialArgs = parsePartialArgs(args);
    if (partialArgs) yield partialArgs;
  }
}

function parsePartialArgs(args: string): ToolArgsSnapshot | undefined {
  let parsed: unknown;

  try {
    parsed = parsePartialJson(args);
  } catch {
    return undefined;
  }

  return isToolArgsSnapshot(parsed) ? parsed : undefined;
}

function isToolArgsSnapshot(value: unknown): value is ToolArgsSnapshot {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function defineCRUDTools(
  version: 'v1' | 'v2',
  dataClasses: DataClass[],
) {
  if (version === 'v1') {
    return defineCRUDToolsV1(dataClasses);
  }

  return defineCRUDToolsV2(dataClasses);
}
